import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, Clock, BadgeCheck, MapPin, ShieldCheck } from "lucide-react";

/**
 * Article highlight block on the landing page.
 * Surfaces the medical oxygen SEO article with key bullets and a "read more" CTA.
 * Pulls users searching long-tail "venta de oxígeno medicinal Zihuatanejo".
 */
export default function ArticleHighlight() {
  return (
    <section
      id="articulo-oxigeno-medicinal"
      data-testid="article-highlight"
      aria-label="Artículo: Venta de Oxígeno Medicinal en Zihuatanejo"
      className="relative bg-white py-20 lg:py-24 overflow-hidden border-y border-slate-200"
    >
      <div className="absolute inset-0 stripe-accent opacity-[0.04] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
          {/* Left: copy */}
          <div>
            <span className="inline-flex items-center gap-2 font-mono-cima text-[10px] uppercase tracking-[0.3em] text-[var(--cima-red)]">
              <BookOpen className="w-3.5 h-3.5" />
              Guía de servicio
            </span>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[var(--cima-navy)] leading-[1.05]">
              Venta de oxígeno medicinal en{" "}
              <span className="text-[var(--cima-red)]">Zihuatanejo</span>.
            </h2>
            <p className="mt-4 text-slate-700 leading-relaxed max-w-2xl">
              Cuando un paciente necesita apoyo respiratorio en casa, no hay margen
              para improvisar. Te explicamos cómo elegir bien entre{" "}
              <strong>venta, renta y recarga</strong> de oxígeno medicinal — con
              atención 24/7, entrega a domicilio y respaldo como{" "}
              <strong>Distribuidor Autorizado INFRA</strong>.
            </p>

            <ul className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
              {[
                { icon: BadgeCheck, t: "Distribuidor INFRA autorizado" },
                { icon: Clock, t: "Atención telefónica 24/7" },
                { icon: MapPin, t: "Servicio local en Zihuatanejo e Ixtapa" },
                { icon: ShieldCheck, t: "Calidad medicinal certificada" },
              ].map((b, i) => {
                const Icon = b.icon;
                return (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <span className="w-7 h-7 shrink-0 bg-[var(--cima-surface)] text-[var(--cima-red)] flex items-center justify-center">
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    {b.t}
                  </li>
                );
              })}
            </ul>

            <Link
              to="/articulos/venta-oxigeno-medicinal-zihuatanejo"
              data-testid="article-highlight-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[var(--cima-navy)] hover:bg-[var(--cima-red)] text-white px-6 py-3.5 font-display font-bold text-sm uppercase tracking-wide transition-colors"
            >
              Leer guía completa
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Right: stylised preview card */}
          <div className="relative">
            <div className="absolute -top-4 -right-4 w-32 h-32 stripe-accent opacity-20" />
            <article className="relative bg-[var(--cima-surface)] border border-slate-200 p-6 lg:p-8 shadow-sm">
              <div className="flex items-center gap-2 font-mono-cima text-[10px] uppercase tracking-[0.3em] text-slate-500">
                <span className="w-1.5 h-1.5 bg-[var(--cima-red)]" />
                Artículo · Oxígeno Medicinal
              </div>
              <h3 className="mt-3 font-display font-bold text-xl text-[var(--cima-navy)] leading-snug">
                Qué debe ofrecer una venta de oxígeno medicinal confiable
              </h3>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                No se reduce a entregar un tanque. Incluye control en el manejo,
                claridad sobre la modalidad de suministro y capacidad real de
                respuesta. Te ayudamos a evaluar las{" "}
                <strong>4 claves antes de contratar</strong>: respaldo, cobertura local,
                disponibilidad 24/7 y claridad comercial.
              </p>
              <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                {[
                  { n: "24/7", l: "Atención" },
                  { n: "1972", l: "Desde" },
                  { n: "INFRA", l: "Autorizado" },
                ].map((s, i) => (
                  <div key={i} className="bg-white border border-slate-200 py-3">
                    <div className="font-display font-extrabold text-base text-[var(--cima-red)]">
                      {s.n}
                    </div>
                    <div className="font-mono-cima text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/articulos/venta-oxigeno-medicinal-zihuatanejo"
                className="mt-6 inline-flex items-center gap-2 text-sm font-display font-bold text-[var(--cima-red)] hover:text-[var(--cima-red-deep)]"
              >
                Continuar leyendo
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
